import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { matchPath } from 'react-router-dom';

import * as analytics from '@adsk/acs-web-analytics';

import { ANALYTICS_TYPES } from '../analytics';
import {
  ANDROID_APP_PACAKGE,
  DEEP_LINKS_PARAM_KEY,
  IOS_APP_SCHEME,
  IOS_APP_STORE_URL,
  MOBILE_SUPPORTED_PATH_PATTERNS,
  TIMEOUT_TO_OPEN_STORE_URL_IN_MS,
} from '../components/OpenInMobileApp/consts';
import { ToolRoute } from '../envs/types';
import { getRemoteRoute } from '../utils/getCurrentRoute';
import getPossibleMobilePlatformInformation, {
  SUPPORTES_PLATFORMS,
} from '../utils/getPossibleMobilePlatformInformation';

type UseOpenInMobileAppReturnValue = {
  showOpenInMobileApp: boolean;
  continueInWeb: () => void;
  continueInApp: () => void;
};

const useOpenInMobileApp = (): UseOpenInMobileAppReturnValue => {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showOpenInMobileApp, setShowOpenInMobileApp] = useState(false);
  const { platform, isMobile } = getPossibleMobilePlatformInformation();
  const currentToolId = getRemoteRoute<ToolRoute>(window.location.pathname)
    ?.layoutProps?.currentToolId;

  const appendParamToWindowLocationSearch = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(DEEP_LINKS_PARAM_KEY, 'true');
    window.location.search = searchParams.toString();
  }, []);

  const getSearchParam = useCallback((key: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(key);
  }, []);

  const openAndroidApp = useCallback(() => {
    const intentURI = `intent://acc.autodesk.com${window.location.pathname}/#Intent;scheme=https;package=${ANDROID_APP_PACAKGE};end`;

    window.location.replace(intentURI);
  }, []);

  const openIOSApp = useCallback(() => {
    const appUrl = `${IOS_APP_SCHEME}://${window.location.href}`;

    window.location.replace(appUrl);

    timeout.current = setTimeout(() => {
      // scheme is not registered (app not installed)
      window.location.replace(IOS_APP_STORE_URL);
    }, TIMEOUT_TO_OPEN_STORE_URL_IN_MS);
  }, []);

  const closeOpenInMobileApp = useCallback(
    ({ openMobileApp }: { openMobileApp?: boolean }) => {
      if (openMobileApp) {
        if (platform === SUPPORTES_PLATFORMS.ANDROID) {
          openAndroidApp();
        } else if (platform === SUPPORTES_PLATFORMS.IOS) {
          openIOSApp();
        }
      } else {
        setShowOpenInMobileApp(false);
        appendParamToWindowLocationSearch();
      }

      analytics.track(ANALYTICS_TYPES.DEEP_LINK_CLICK, {
        source_url: window.location.href,
        tool_id: (currentToolId as string) || 'N/A',
        intermediate_page_displayed: true,
        user_continued_to: openMobileApp ? 'app' : 'web',
        platform,
      });
    },
    [
      platform,
      currentToolId,
      openAndroidApp,
      openIOSApp,
      appendParamToWindowLocationSearch,
    ],
  );

  useLayoutEffect(() => {
    const isRoutePathSupported = MOBILE_SUPPORTED_PATH_PATTERNS.some(
      (pattern) => matchPath(pattern, window.location.pathname),
    );

    if (
      isRoutePathSupported &&
      isMobile &&
      getSearchParam(DEEP_LINKS_PARAM_KEY) !== 'true'
    ) {
      setShowOpenInMobileApp(true);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, [isMobile, closeOpenInMobileApp, getSearchParam]);

  return {
    showOpenInMobileApp,
    continueInWeb: () => closeOpenInMobileApp({ openMobileApp: false }),
    continueInApp: () => closeOpenInMobileApp({ openMobileApp: true }),
  };
};

export default useOpenInMobileApp;
